import { readData, uploadRequest } from './uploadRequest'

// Upload of a single-file model (.4dv / .ply / .splat / .splatv) in parts
// through the Worker (backend routes/model-multipart.ts). ModelUploader uses it
// when upload/start returned no presigned R2 URL and the file is too large for
// one proxy PUT: Cloudflare rejects request bodies over 100 MB at the edge with
// an HTML 413 that has no CORS headers, which the browser only reports as a
// network or CORS error.

const DEFAULT_PROXY_PUT_MAX_BYTES = 90 * 1024 * 1024

/** Largest file sent as one proxy PUT; anything larger goes up in parts.
 * VITE_UPLOAD_PROXY_MAX_BYTES can only lower it, so a test can send a small
 * file in parts; production builds leave it unset. */
export const PROXY_PUT_MAX_BYTES = (() => {
  const override = Number(import.meta.env.VITE_UPLOAD_PROXY_MAX_BYTES)
  return Number.isFinite(override) && override > 0
    ? Math.min(override, DEFAULT_PROXY_PUT_MAX_BYTES)
    : DEFAULT_PROXY_PUT_MAX_BYTES
})()

// R2 needs every part but the last to be at least 5 MiB and the same size; the
// Worker accepts parts up to 64 MiB.
const PART_BYTES = 32 * 1024 * 1024
const PARTS_IN_FLIGHT = 3
// A part is retried for about a minute (waits of 1, 2, 4, 8, 15, 15 and 15 s)
// so that a short network drop does not throw away the parts already sent.
const PART_ATTEMPTS = 8
const PART_MAX_DELAY_MS = 15_000
const MAX_PARTS = 10_000

interface ModelMultipartOptions {
  apiBase: string
  uploadId: string
  token: string | null
  adminEmail: string | null | undefined
  /** Bytes sent so far (finished parts plus the parts in flight). */
  onProgress?: (sentBytes: number, totalBytes: number) => void
  /** A one-line description of what is happening, for the progress row. */
  onStatus?: (message: string) => void
}

const mb = (bytes: number) => `${(bytes / 1e6).toFixed(1)} MB`
const parts = (count: number) => `${count} part${count === 1 ? '' : 's'}`

/** Upload `file` as models/{uploadId}/original{ext} in 32 MiB parts, three at a
 * time. Each part is retried on network errors and 5xx for about a minute; if
 * a part still fails, or finishing fails, the other parts are cancelled, the
 * upload is cancelled on the server (which also removes the file if it was
 * stored after all) and the error is thrown. The object key is chosen by the
 * server from the upload session. */
export async function uploadModelMultipart(
  file: Blob,
  { apiBase, uploadId, token, adminEmail, onProgress, onStatus }: ModelMultipartOptions,
): Promise<{ key: string; partCount: number }> {
  if (file.size === 0) throw new Error('The file is empty')
  const partCount = Math.ceil(file.size / PART_BYTES)
  if (partCount > MAX_PARTS) throw new Error('The file exceeds the multipart upload size limit')

  const base = `${apiBase.replace(/\/$/, '')}/api/v2/admin/models/proxy/upload/${encodeURIComponent(uploadId)}/multipart`
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(adminEmail ? { 'X-Admin-Email': adminEmail } : {}),
  }
  const binaryHeaders = { ...headers, 'Content-Type': 'application/octet-stream' }
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' }

  const start = await uploadRequest(base, {
    method: 'POST', headers: jsonHeaders, body: '{}',
  }, 'Starting the upload')
  const { multipartUploadId } = await readData<{ multipartUploadId: string }>(start, 'Starting the upload')
  if (typeof multipartUploadId !== 'string' || !multipartUploadId) {
    throw new Error('Starting the upload failed: missing multipart upload id')
  }
  const session = `${base}/${encodeURIComponent(multipartUploadId)}`

  const controller = new AbortController()
  const etags: string[] = []
  const inFlight = new Map<number, number>() // part number -> bytes sent in its current attempt
  let doneBytes = 0
  let doneParts = 0
  let nextPart = 1
  let failure: { error: unknown } | null = null

  const report = () => {
    let sent = doneBytes
    for (const loaded of inFlight.values()) sent += loaded
    onProgress?.(sent, file.size)
  }
  const describe = () => onStatus?.(
    `Uploading ${mb(file.size)} in ${parts(partCount)} (${doneParts} of ${partCount} done)…`)

  // Each runner takes the next part until none are left or one has failed.
  const runner = async () => {
    while (!failure && nextPart <= partCount) {
      const partNumber = nextPart++
      const offset = (partNumber - 1) * PART_BYTES
      const body = file.slice(offset, offset + PART_BYTES)
      const stage = `Upload of part ${partNumber}/${partCount}`
      inFlight.set(partNumber, 0)
      try {
        const response = await uploadRequest(`${session}/part/${partNumber}`, {
          method: 'PUT', headers: binaryHeaders, body, signal: controller.signal,
        }, stage, (attempt) => {
          inFlight.set(partNumber, 0)
          report()
          onStatus?.(`Part ${partNumber}/${partCount} failed; retrying (attempt ${attempt} of ${PART_ATTEMPTS})…`)
        }, {
          attempts: PART_ATTEMPTS,
          maxDelayMs: PART_MAX_DELAY_MS,
          onUploadProgress: (loaded) => {
            if (!inFlight.has(partNumber)) return
            inFlight.set(partNumber, loaded)
            report()
          },
        })
        const part = await readData<{ partNumber: number; etag: string }>(response, stage)
        if (part.partNumber !== partNumber || typeof part.etag !== 'string' || !part.etag) {
          throw new Error(`${stage} failed: invalid server response`)
        }
        etags[partNumber - 1] = part.etag
        inFlight.delete(partNumber)
        doneBytes += body.size
        doneParts++
        report()
        describe()
      } catch (error) {
        inFlight.delete(partNumber)
        // The first failure wins; the parts it cancels fail with AbortError.
        if (!failure) {
          failure = { error }
          controller.abort()
        }
        return
      }
    }
  }

  let completeSent = false
  try {
    describe()
    report()
    await Promise.all(Array.from({ length: Math.min(PARTS_IN_FLIGHT, partCount) }, runner))
    if (failure) throw (failure as { error: unknown }).error

    onStatus?.(`Finishing the upload (${parts(partCount)})…`)
    const uploaded = etags.map((etag, index) => ({ partNumber: index + 1, etag }))
    // A retry of complete after a lost response is answered with the stored
    // file, so retrying it is safe.
    completeSent = true
    const response = await uploadRequest(`${session}/complete`, {
      method: 'POST', headers: jsonHeaders, body: JSON.stringify({ parts: uploaded }),
    }, 'Finishing the upload', () => onStatus?.('Finishing the upload failed; retrying…'))
    // The server compares the stored size with the size given at upload/start
    // and discards the file (400) when they differ.
    const completed = await readData<{ key: string }>(response, 'Finishing the upload')
    return { key: completed.key, partCount }
  } catch (error) {
    controller.abort()
    // Cancel on the server: abort the multipart upload so no parts are kept,
    // and remove the file if a complete went through unseen. Best effort: the
    // original error is the useful one.
    const cancelled = await fetch(session, { method: 'DELETE', headers })
      .then((response) => response.ok)
      .catch(() => false)
    const message = (error instanceof Error ? error.message : String(error)).replace(/\.?$/, '.')
    throw new Error(cancelled
      ? `${message} The upload was cancelled and nothing was stored.`
      : completeSent
        ? `${message} The upload could not be cancelled, so the file may have been stored anyway; upload it again.`
        : `${message} The unfinished upload could not be cancelled; R2 removes it automatically within 7 days.`)
  }
}
